import type { WatchQueryFetchPolicy } from "@apollo/client";
import {
  ColumnDef,
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Box, Text } from "@chakra-ui/react";

import { DocumentType, gql } from "__generated__/gql";
import FullPageTable, {
  FullPageTableProps,
  useFullPageTableProps,
} from "components/Shared/ChakraReactTable/FullPageTable";
import useFixedLayout from "components/Shared/ChakraReactTable/useFixedLayout";
import { usePrepApolloData } from "components/Shared/ChakraReactTable/utils";
import { useAdminQuery } from "utils/apollo/hooks/admin";
import { RetailerFilter, RetailersOrderBy } from "__generated__/gql/graphql";
import usePagination, {
  usePaginationReset,
} from "components/Shared/ChakraReactTable/usePagination";
import useSelectableRow from "components/Shared/ChakraReactTable/useSelectableRow";
import useGQLSorting, {
  GQLOrderByEnumsById,
} from "components/Shared/ChakraReactTable/useGQLSorting";
import {
  DEFAULT_COLUMNS,
  DEFAULT_PINNED_COLUMNS,
  orderByEnumsById as baseOrderByEnumsById,
} from "components/Shared/RetailerDirectory/Table";
import { formatJustifiedDateTimeCell } from "components/Shared/ChakraReactTable/formatters";
import IsCustomBadge from "components/Shared/IsCustomBadge";
import useArchivedRowProps from "components/Shared/ChakraReactTable/useArchivedRowProps";

const query = gql(/* GraphQL */ `
  query AdminManageRetailersTable(
    $filter: RetailerFilter
    $orderBy: [RetailersOrderBy!] = [CREATED_AT_ASC]
    $limit: Int = 10
    $offset: Int = 0
  ) {
    retailers(
      filter: $filter
      first: $limit
      offset: $offset
      orderBy: $orderBy
    ) {
      totalCount
      nodes {
        id
        createdAt
        isArchived
        isCustom
        forBrand {
          id
          name
        }
        retailerType
        ...RetailersDirectoryTableRow
      }
    }
  }
`);

type Datum = NonNullable<
  DocumentType<typeof query>["retailers"]
>["nodes"][number];

const col = createColumnHelper<Datum>();

const columns = [
  ...(DEFAULT_COLUMNS as ColumnDef<Datum, unknown>[]),
  col.accessor((r) => r.forBrand?.name, {
    id: "customBrand",
    header: "Custom?",
    cell: (cell) => (
      <>
        <IsCustomBadge isCustom={cell.row.original.isCustom} />
        <Box fontSize="xs" color="gray.600" isTruncated>
          {cell.row.original.forBrand?.name}
        </Box>
      </>
    ),
    meta: { props: { textAlign: "center" } },
  }),
  col.accessor("createdAt", {
    header: "Added",
    cell: (cell) => formatJustifiedDateTimeCell(cell),
    meta: { props: { textAlign: "right" } },
  }),
];

export const orderByEnumsById: GQLOrderByEnumsById<RetailersOrderBy> = {
  ...baseOrderByEnumsById,
  customBrand: [
    RetailersOrderBy.FOR_BRAND__NAME_ASC,
    RetailersOrderBy.FOR_BRAND__NAME_DESC,
  ],
  createdAt: [
    RetailersOrderBy.CREATED_AT_ASC,
    RetailersOrderBy.CREATED_AT_DESC,
  ],
};

export type AdminManageRetailersTableProps = Pick<
  FullPageTableProps<Datum>,
  "scrollContainerProps" | "tableContainerProps" | "tableControlsFooterProps"
> & {
  defaultPageSize?: number;
  filter?: RetailerFilter;
  isReady?: boolean;
  nextFetchPolicy?: WatchQueryFetchPolicy;
  onSelectedRetailerIdChange: (newRetailerId: UUID) => void;
  selectedRetailerId: UUID | null | undefined;
};

export default function AdminManageRetailersTable({
  defaultPageSize = 25,
  filter,
  isReady = true,
  nextFetchPolicy,
  onSelectedRetailerIdChange,
  scrollContainerProps,
  selectedRetailerId,
  tableContainerProps,
  tableControlsFooterProps,
}: AdminManageRetailersTableProps) {
  const {
    pagination,
    onPaginationChange,
    limitOffset,
    getPageCount,
    setPageIndex,
  } = usePagination({ pageSize: defaultPageSize });

  const { sorting, onSortingChange, orderBy } = useGQLSorting(
    orderByEnumsById,
    [{ id: "createdAt", desc: false }],
  );

  const isPendingFilterReset = usePaginationReset(
    filter,
    setPageIndex,
    pagination.pageIndex,
  );

  const isPendingSortingReset = usePaginationReset(
    sorting,
    setPageIndex,
    pagination.pageIndex,
  );

  const {
    data: rawData,
    loading: isLoading,
    refetch,
  } = useAdminQuery(query, {
    nextFetchPolicy,
    skip: !isReady || isPendingFilterReset || isPendingSortingReset,
    variables: { filter, orderBy, ...limitOffset },
  });

  const tableInstance = useReactTable({
    columns,
    data: usePrepApolloData(rawData?.retailers?.nodes),
    getCoreRowModel: getCoreRowModel(),
    onPaginationChange,
    onSortingChange,
    manualPagination: true,
    manualSorting: true,
    enableSorting: true,
    state: { columnPinning: DEFAULT_PINNED_COLUMNS, pagination, sorting },
    pageCount: getPageCount(rawData?.retailers?.totalCount),
  });

  const tableProps = useFullPageTableProps<Datum>([
    useFixedLayout(tableInstance),
    useSelectableRow({
      selected: selectedRetailerId,
      onSelectedChange: onSelectedRetailerIdChange,
    }),
    useArchivedRowProps(),
    { tableProps: { variant: "condensed" } },
  ]);

  return (
    <FullPageTable
      tableInstance={tableInstance}
      {...tableProps}
      {...{
        scrollContainerProps,
        tableContainerProps,
        tableControlsFooterProps,
      }}
      isLoading={isLoading || !isReady || typeof window === "undefined"}
      noRowsPlaceholder={<Text>No retailers found.</Text>}
      totalCount={rawData?.retailers?.totalCount}
      resultTypeLabel="retailers"
      onRefresh={refetch}
    />
  );
}
